import * as XLSX from 'xlsx'

export type StringMap = {
	id: string
	target: string
	sources: string
}

export type StringMapError = {
	id: string
	string: string
}

export type StringMapObject = {
	[key: string]: StringMap[]
}

const defaultUnitStringMaps = [
	{ id: 'EACH', target: 'EACH', sources: 'EA\nEACH' },
	{ id: 'FULL', target: 'FULL', sources: 'CA\nCASE\nCS' },
	{ id: 'HALF', target: 'HALF', sources: 'HALF' },
	{ id: 'SPLITWEIGHT', target: 'SPLITWEIGHT', sources: '' },
	{ id: 'WEIGHT', target: 'WEIGHT', sources: 'LB\nPOUNDS\nWEIGHT\nWHT' },
]

export const fieldCategoryValues = [
	'Brand',
	'Category',
	'Customer',
	'Date',
	'Description',
	'DPC',
	'Distributor',
	'ExtendedPrice',
	'ExtendedPriceEach',
	'Manufacturer',
	'MPC',
	'Pack',
	'Quantity',
	'QuantityEach',
	'Unit',
	'UnitPrice',
	'Weight',
]

const excelFileRangeValues = {
	row: 9,
	column: 100,
}

export function parseExcelFile(file: File): Promise<any[][]> {
	return new Promise((resolve, reject) => {
		const reader = new FileReader()
		reader.onload = (event) => {
			try {
				// Parse data
				const bstr = event.target.result as string
				const workBook = XLSX.read(bstr, { type: 'binary' })
				// Identify first sheet
				const workSheetName = workBook.SheetNames[0]
				const workSheet = workBook.Sheets[workSheetName]
				// Get first 10 rows of data with 100 columns
				const fileData: any[][] = XLSX.utils.sheet_to_json(workSheet, {
					header: 1,
					range: {
						// Starting index
						s: { r: 0, c: 0 },
						// Ending index (currently gets first 10 rows and first 100 columns)
						e: { r: excelFileRangeValues.row, c: excelFileRangeValues.column },
					},
				})
				if (!fileData.length || fileData.every((row) => !row.length))
					throw Error(
						'No data found in uploaded Excel file (Excel file must have data in the first 10 rows and the first 100 columns)'
					)
				resolve(fileData)
			} catch (error) {
				reject(error)
			}
		}
		reader.readAsArrayBuffer(file)
	})
}

/*
 * Merges source strings for StringMaps that have the same target string
 * Then removed duplicate and empty source strings from each StringMap
 */
export function cleanStringMaps(stringMaps: StringMap[]) {
	// Merge String Maps that have the same target value, keeping both sources from both
	const mergedMap = new Map()
	stringMaps
		.filter((stringMap) => stringMap.target && stringMap.sources)
		.forEach((stringMap: StringMap) => {
			if (mergedMap.has(stringMap.target)) {
				const existingItem = mergedMap.get(stringMap.target)
				existingItem.sources += `\n${stringMap.sources}`
			} else {
				mergedMap.set(stringMap.target, { ...stringMap })
			}
		})

	const mergedStringMaps: StringMap[] = Array.from(mergedMap.values())

	// Remove duplicate and empty values from sources
	const filteredStringMaps = []
	mergedStringMaps.forEach((stringMap: StringMap) => {
		const uniqueSources = new Set()
		const dedupedSources = []
		stringMap.sources.split('\n').forEach((string) => {
			if (string && !uniqueSources.has(string) && string.trim()) {
				uniqueSources.add(string)
				dedupedSources.push(string)
			}
		})
		filteredStringMaps.push({
			...stringMap,
			sources: dedupedSources.join('\n'),
		})
	})
	return filteredStringMaps
}

/*
 * Adds default blank StringMap for any Unit maps that are not currently saved so
 * they can be edited in the UnitStringMappingDialog component
 * ('EACH', 'FULL', 'HALF', 'SPLITWEIGHT', 'WEIGHT')
 */
export function getUnitMapsState(savedUnitMaps: StringMap[]) {
	const unitMapState = [...savedUnitMaps]
	const unitTargets = ['EACH', 'FULL', 'HALF', 'SPLITWEIGHT', 'WEIGHT']
	unitTargets.forEach((target) => {
		const existingUnitMap = unitMapState.find((unitMap) => unitMap.id === target)
		if (!existingUnitMap) unitMapState.push({ id: target, target, sources: '' })
	})
	return unitMapState
}

/*
 * Returns a StringMapError object with the first duplicate source string found
 */
export function checkDuplicateSources(stringMaps: StringMap[]): StringMapError {
	let stringMapWithDuplicate = null
	const uniqueSourceStrings = new Set()
	stringMaps.forEach((stringMap: StringMap) => {
		const sourceStrings = stringMap.sources.split('\n')
		sourceStrings.forEach((string) => {
			if (!uniqueSourceStrings.has(string)) {
				return uniqueSourceStrings.add(string)
			}
			if (string && !stringMapWithDuplicate) {
				stringMapWithDuplicate = { id: stringMap.id, string }
			}
		})
	})
	return stringMapWithDuplicate
}

/*
 * Converts the FileLayoutEditor state to a String after clicking "Save"
 */
export function fileLayoutStateToStr(
	startRow: number,
	stringMapObject: StringMapObject,
	columnMaps: { [key: string]: number }
): string {
	let stateString = `StartRow:${startRow || '1'}\n`
	Object.entries(columnMaps).forEach(([key, value]) => {
		let categoryString = `Field:${key}=${value}`
		if (stringMapObject[key]) {
			stringMapObject[key].forEach((stringMap: StringMap) => {
				if (stringMap.sources) {
					categoryString = categoryString.concat(
						`|${stringMap.target}:${stringMap.sources.split('\n').join(',')}`
					)
				}
			})
		}
		stateString = stateString.concat(`${categoryString}\n`)
	})
	return stateString.trimEnd()
}

export function getStartRowFromString(string: string): number {
	// Matches any sequence of digits that follows the string "StartRow:"
	const startRowMatch = string.match(/(?<=StartRow:)\d+/)
	return startRowMatch ? Number(startRowMatch) : 1
}

export function getColumnMapsFromString(string: string): { [key: string]: number } {
	const columnMaps = {}
	// Array of strings that follow the pattern: "Field:[word]=[digits]"
	const lines = string.split('\n').filter((line) => line.match(/Field:\w+=\d+/))
	lines.forEach((line) => {
		const columnString = line.split('|')[0] // "Field:[columnCategory]=[columnIndex]"
		const [fieldName, columnIndex] = columnString.replace('Field:', '').split('=')
		if (fieldCategoryValues.includes(fieldName)) columnMaps[fieldName] = Number(columnIndex)
	})
	return columnMaps
}

export function getStringMapsFromString(string: string): StringMapObject {
	const stringMapObj: StringMapObject = {}
	// Filter out StartRow string and Field strings that cannot have stringMaps
	const lines = string
		.split('\n')
		.filter((line) => line.match(/Field:(Category|Customer|Unit|Distributor|Manufacturer)=\d+/))

	lines.forEach((line) => {
		const stringMapArr: StringMap[] = []
		const fieldName = line.slice(line.indexOf(':') + 1, line.indexOf('='))
		const targetAndSources = line.split('|').slice(1) // Array of "[target]:[source1,source2,...]"
		targetAndSources.forEach((targetAndSource) => {
			const [targetString, sourcesString] = targetAndSource.split(':')
			const sources = sourcesString.split(',').join('\n')
			stringMapArr.push({ id: targetString, target: targetString, sources })
		})
		if (stringMapArr.length) stringMapObj[fieldName] = stringMapArr
	})
	if (!stringMapObj.Unit) stringMapObj.Unit = defaultUnitStringMaps
	return stringMapObj
}
